import { useId, useRef } from 'react';
import { cn } from '@utils/cn';
import { useTranslation } from 'next-i18next';
import { Icon, Tooltip } from '../../../../design-system/components';

type MaxQuantitySelectorProps = {
  itemCount: number;
  maxQuantity: number;
  selectedCount: number;
  onChange: (maxQuantity: number) => void;
};

export const MaxQuantitySelector = ({
  itemCount,
  maxQuantity,
  onChange,
  selectedCount,
}: MaxQuantitySelectorProps) => {
  const { t } = useTranslation('checkout');
  const iconButtonRef = useRef<HTMLButtonElement>(null);
  const tooltipId = useId();

  if (selectedCount === 0) {
    return null;
  }

  return (
    <section className="flex w-full flex-col gap-4">
      <div className="flex flex-col text-neutral-6">
        <p className="font-bold">{t('orderReview.maxWins.title')}</p>
        <div className="flex cursor-pointer flex-row items-center gap-1">
          <p>{t('orderReview.maxWins.description')}</p>
          <button
            ref={iconButtonRef}
            type="button"
            className="flex border-0 bg-transparent p-0"
            aria-label={t('orderReview.maxWins.tooltip')}
            aria-describedby={tooltipId}
          >
            <Icon name="ic_info" />
          </button>
        </div>
      </div>
      <div className="grid grid-cols-6 gap-3">
        {Array.from({ length: itemCount }, (_, index) => {
          const value = index + 1;
          const isSelected = maxQuantity === value;
          const isDisabled = value > selectedCount;
          return (
            <button
              key={value}
              className={cn(
                'rounded-sm border border-divider/20 p-4 text-center text-sm transition-colors',
                isSelected && 'border-neutral-6 ring-1 ring-neutral-6',
                !isSelected && 'text-neutral-6 hover:border-divider/40',
                isDisabled && 'cursor-not-allowed opacity-30',
              )}
              disabled={isDisabled}
              onClick={() => onChange(value)}
              type="button"
            >
              {value}
            </button>
          );
        })}
      </div>
      <Tooltip
        id={tooltipId}
        parentRef={iconButtonRef}
        selectionRef={iconButtonRef}
      >
        {t('orderReview.maxWins.tooltip')}
      </Tooltip>
    </section>
  );
};
